import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol, ToastController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { MarvDateService } from '@marv/services';
import { MarvButtonComponent, MarvSelectComponent, MarvSelectItem, MarvToggleComponent, MarvSectionTitleComponent } from '@marv/components';
import { PermanentAgentService, PermanentPlanningService } from '@app/services';

export class PermanentPlanningDuplicate {
    agentId: number;
    week: Date;
    constructor(agentId: number, week: Date){
        this.agentId = agentId;
        this.week = week;
    }
}

@Component({
    selector: 'app-permanent-planning-duplicate',
    templateUrl: './duplicate.component.html',
    styleUrls: ['./duplicate.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvSelectComponent), forwardRef(() => MarvToggleComponent), 
        forwardRef(() => MarvSectionTitleComponent)
    ]
})
export class PermanentPlanningDuplicateComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'agentId', required : true}) agentId: number;
    @Input({alias : 'week', required : true}) week: Date;
    @Output() onDuplicatePermanentPlanning = new EventEmitter<PermanentPlanningDuplicate|null>();

    isFetchingSave: boolean = false;
    permanentPlanningDuplicateForm: FormGroup = new FormGroup({
        srcWeek: new FormControl(null, [Validators.required]),
        agentsId: new FormControl([], {nonNullable: true, validators: [Validators.required]}),
        days: new FormControl([], {nonNullable: true, validators: [Validators.required]}),
        copyEvents: new FormControl(false, {nonNullable: true}),
        destWeeks: new FormControl(null, [Validators.required])
    });
    weeksToCopyItems: Array<MarvSelectItem> = []; 
    agentsItems: Array<MarvSelectItem> = []; 
    daysItems: Array<MarvSelectItem> = []; 
    destinationWeeks: Array<MarvSelectItem> = []; 
    updateSrcWeekSubscription: Subscription;

    constructor(
        public translateService: TranslateService,
        private toastController: ToastController,
        private permanentAgentService: PermanentAgentService,
        private permanentPlanningService: PermanentPlanningService
    ) { }

    ngOnInit() {
        this.weeksToCopyItems = this.buildWeeks(3, 3);
        this.destinationWeeks = this.buildWeeks(0, 6);
        
        this.updateSrcWeekSubscription = this.permanentPlanningDuplicateForm.controls['srcWeek'].valueChanges.subscribe(value => {            
            this.setDaysItems();
        });        
    }
    
    ngOnDestroy() {           
        if(this.updateSrcWeekSubscription){
            this.updateSrcWeekSubscription.unsubscribe();
        }        
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['operationId']){
            let agentsItems: Array<MarvSelectItem> = [];
            if(this.operationId){
                let agents = await this.permanentAgentService.loadPermanentsAgents(this.operationId);
                for(let agent of agents){
                    agentsItems.push(new MarvSelectItem(agent.name,agent.id));
                }
            }
            this.agentsItems = agentsItems;
        }
        
        if(changes['agentId']){
            this.permanentPlanningDuplicateForm.controls['agentsId'].setValue((this.agentId ? [this.agentId] : []), {emitEvent: false});
        }
        
        if(changes['week']){
            let weekStr: string|null = null;
            if(this.week){
                weekStr = moment(this.week).format('YYYY-MM-DD');
            }
            this.permanentPlanningDuplicateForm.controls['srcWeek'].setValue(weekStr, {emitEvent: false});
            this.setDaysItems();
        }
        
    }
    
    buildWeeks(nbMonthInPast: number, nbMonthInFuture: number): Array<MarvSelectItem>{   
        let weeks: Array<MarvSelectItem> = [];        
        let startWeek = moment();        
        if (nbMonthInPast != 0){
            startWeek.add(nbMonthInPast * -1, 'months');
        }
        else{
            startWeek.add(1, 'weeks');
        }
        let endWeek = moment().add(nbMonthInFuture, 'months');
        while (startWeek.isBefore(endWeek)) {
            let startOfWeek = moment(MarvDateService.GetFirstDayOfWeek(startWeek.toDate()));
            let weekNo = startOfWeek.format('YYYY-MM-DD');
            let weekDetails = 'S'+startOfWeek.format('W - ddd DD/MM/YYYY');
            weeks.push(new MarvSelectItem(weekDetails, weekNo));
            startWeek.add(1, 'weeks');
        }
        return weeks;
    }

    buildWeekDays(week: Date): Array<MarvSelectItem>{   
        let days: Array<MarvSelectItem> = [];
        if(week){
            let startDay =  moment(MarvDateService.GetFirstDayOfWeek(week));
            let endDay = moment(MarvDateService.GetLastDayOfWeek(week));
            while (startDay.isBefore(endDay) || startDay.day() === endDay.day()) {
                let day = MarvDateService.FromDate(startDay.toDate());
                let dayCaption = startDay.format('ddd DD MMM');
                if(day){
                    days.push(new MarvSelectItem(dayCaption, day));
                }
                startDay.add(1, 'days');
            }
        }
        return days;
    }
    
    setDaysItems(){
        let week = moment(this.permanentPlanningDuplicateForm.controls['srcWeek'].value);
        this.daysItems = this.buildWeekDays(week.toDate());
        let days: Array<number> = [];
        for(let d of this.daysItems){
            days.push(parseInt(d.value.toString()));
        }
        this.permanentPlanningDuplicateForm.controls['days'].setValue(days, {emitEvent: false});
    }

    async duplicate() {
        if(this.permanentPlanningDuplicateForm.valid){            
            this.isFetchingSave = true;
            
            let agentsId = this.permanentPlanningDuplicateForm.controls['agentsId'].value;
            let destWeeksStr: Array<string> = []
            let destWeeks = this.permanentPlanningDuplicateForm.controls['destWeeks'].value;
            for(let w of destWeeks){
                destWeeksStr.push(moment(w).format('W-YYYY'));
            }
            let days = this.permanentPlanningDuplicateForm.controls['days'].value;
            let copyEvents = this.permanentPlanningDuplicateForm.controls['copyEvents'].value;
            let week = moment(destWeeks[0]);
            
            let statusDuplicate = await this.permanentPlanningService.duplicatePlanning(this.operationId, agentsId, days, destWeeksStr, copyEvents);
            
            if(statusDuplicate){
                let permanentPlanningDuplicate = new PermanentPlanningDuplicate(agentsId[0], week.toDate());
                this.onDuplicatePermanentPlanning.emit(permanentPlanningDuplicate);
                this.resetForm();
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_SUCCESS_DUPLICATE_PLANNING'),
                    duration: 6000,
                    position: 'top',
                    color: 'success'
                });
                await toast.present();
            }
            else{
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_ERROR_DUPLICATE_PLANNING'),
                    duration: 6000,
                    position: 'top',
                    color: 'danger'
                });
                await toast.present();
            }
            
            this.isFetchingSave = false;
        }
        else{
            for (var controlName in this.permanentPlanningDuplicateForm.controls) {
                if(!this.permanentPlanningDuplicateForm.controls[controlName].valid) {
                    this.permanentPlanningDuplicateForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
    
    cancel() {
        this.resetForm();
        this.onDuplicatePermanentPlanning.emit(null);
    }
    
    resetForm() {
        this.permanentPlanningDuplicateForm.reset();
        this.permanentPlanningDuplicateForm.controls['agentsId'].setValue((this.agentId ? [this.agentId] : []), {emitEvent: false});
        let weekStr: string|null = null;
        if(this.week){
            weekStr = moment(this.week).format('YYYY-MM-DD');
        }
        this.permanentPlanningDuplicateForm.controls['srcWeek'].setValue(weekStr, {emitEvent: false});
        this.setDaysItems();
    }
}
